import { formatOneDecimalOrDash, keepOneOrLine, keepWithNumberOrNull } from '@/utils/utils';
import { V2EnergyOverviewCostPostResponse } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { IndexTimeActive } from '../SwitchDate';
import {
  formatName,
  getEnergyMediumColor,
  normalizeChartUnit,
  normalizeFieldSeriesUnit,
  normalizeStackedSeriesUnit,
} from '@/pages/WgqAllMaps/utils';

enum ItemType {
  SUMMIT = 0,
  PEAK,
  PLAIN,
  VALLEY,
  ATTACH,
  BASIC,
}

const itemFieldMap: Record<string, string> = {
  [ItemType.SUMMIT]: 'summitCharges',
  [ItemType.PEAK]: 'peakCharges',
  [ItemType.PLAIN]: 'plainCharges',
  [ItemType.VALLEY]: 'valleyCharges',
  [ItemType.ATTACH]: 'attachCharges',
  [ItemType.BASIC]: 'basicCharges',
};

const itemNameMap: Record<string, string> = {
  [itemFieldMap[ItemType.SUMMIT]]: '尖电费',
  [itemFieldMap[ItemType.PEAK]]: '峰电费',
  [itemFieldMap[ItemType.PLAIN]]: '平电费',
  [itemFieldMap[ItemType.VALLEY]]: '谷电费',
  [itemFieldMap[ItemType.ATTACH]]: '附加电费',
  [itemFieldMap[ItemType.BASIC]]: '基本电费',
};

const itemColorMap: Record<string, string> = {
  [itemFieldMap[ItemType.SUMMIT]]: '#59DBFF',
  [itemFieldMap[ItemType.PEAK]]: '#FF477B',
  [itemFieldMap[ItemType.PLAIN]]: '#6F47FF',
  [itemFieldMap[ItemType.VALLEY]]: '#FFCB47',
  [itemFieldMap[ItemType.ATTACH]]: '#57FB8B',
  [itemFieldMap[ItemType.BASIC]]: '#477BFF',
};

const toolTipColorMap: Record<string, string> = {
  尖电费: '#59DBFF',
  峰电费: '#FF477B',
  平电费: '#6F47FF',
  谷电费: '#FFCB47',
  附加电费: '#57FB8B',
  基本电费: '#477BFF',
};

type DataListItem = Exclude<V2EnergyOverviewCostPostResponse['comprehensiveCost'], undefined>[number];

export const getElecChartOption = (itemDatas?: DataListItem['mediumCostList'], timeResolution?: IndexTimeActive) => {
  const { normalizedData, unit } = normalizeFieldSeriesUnit(itemDatas, {
    valueKeys: ['summitCharges', 'peakCharges', 'plainCharges', 'valleyCharges'],
    baseUnit: '元',
  });

  const timeFormat = timeResolution === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'M月';
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      formatter(params: any) {
        return getTooltip(params, timeFormat, unit);
      },
    },
    xAxis: {
      axisLabel: {
        formatter: function (e: number) {
          return dayjs(e, 'x').format(timeFormat);
        },
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: true,
      },
      data: itemDatas?.map(i => Number(i.ts)),
    },
    yAxis: {
      type: 'value',
      name: unit,
    },
    legend: {
      show: true,
      right: 0,
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 16,
      padding: [0, 8, 0, 0],
      data: getLegendData(normalizedData),
    },
    series: getChartSeries(normalizedData),
  };
};

const getTooltip = (params: any, timeFormat?: string, unit?: string) => {
  const time = dayjs(Number(params[0].axisValue)).format(timeFormat);
  const allCostData = params?.map((i: any) => Number(i.data ?? 0));
  let allCost = allCostData.reduce((a: number, b: number) => a + b, 0).toFixed(1);
  if (params?.every((i: any) => isNil(i.data))) allCost = '--';

  const styleStr = `display:flex;width:240px;justify-content:space-between;font-size: 12px;font-weight: 500;color: rgba(255,255,255,0.65);line-height:17px;margin-top:8px;`;

  const items = params
    .map((i: any) => {
      return `<div style='${styleStr}'>
        <div style='width:40px'>
          <span style='width:8px;height:8px;background:${
            toolTipColorMap[i.seriesName]
          }; border-radius:50%;display:inline-block' ></span>
          <span>${i.seriesName ?? '--'}</span>
        </div>
        <div  style='width:130px; display:flex; justify-content:space-between;align-items:center;'>
          <span style ='color: rgba(255,255,255,0.85);' >${keepOneOrLine(i.data)}${unit}</span>
          <span style ='color: rgba(255,255,255,0.85);'>${i.data ? ((i.data / allCost) * 100).toFixed(1) : '--'}%</span>
        </div>
        </div>`;
    })
    .reverse();

  return (
    `<span style='font-size: 12px;color: rgba(255,255,255,0.65);line-height: 17px;'>${time}</span>` +
    ` <div style='display:flex; justify-content:space-between;align-items:center;font-size: 12px;font-weight: 500;color: rgba(255,255,255,0.85);'><span>总电费</span><span>${allCost}${unit}</span></div>` +
    `${items.join('')}`
  );
};

const getLegendData = (itemDatas?: DataListItem['mediumCostList']) => {
  const fieldList = Object.values(itemFieldMap);
  const data = fieldList
    .filter(v => itemDatas?.some(i => !isNil(i[v as keyof typeof i])))
    .map(v => ({
      name: itemNameMap[v],
      icon: 'rect',
      itemStyle: {
        color: itemColorMap[v],
      },
    }));

  return data;
};

const getChartSeries = (itemDatas?: DataListItem['mediumCostList']) => {
  const fieldList = Object.values(itemFieldMap);
  const series = fieldList
    .filter(v => itemDatas?.some(i => !isNil(i[v as keyof typeof i])))
    .map(v => ({
      type: 'line',
      stack: 'Total',
      color: itemColorMap[v],
      emphasis: {
        focus: 'series',
      },
      areaStyle: {
        opacity: 0.4,
      },
      name: itemNameMap[v],
      data: itemDatas?.map(i => i[v as keyof typeof i]),
    }));
  return series;
};

// 综合用能
export const getAllChartOption = (
  itemDatas?: DataListItem['mediumCostList'],
  timeResolution?: IndexTimeActive,
  energyMediumColorMap?: Record<string, string>
) => {
  const { unit, normalizedData } = normalizeStackedSeriesUnit(itemDatas, {
    seriesKey: 'detailList',
    valueKey: 'charges',
    baseUnit: '元',
  });

  const timeFormat = timeResolution === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'M月';
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      formatter(params: any) {
        return getTooltipByAll(params, timeFormat, unit);
      },
    },
    xAxis: {
      axisLabel: {
        formatter: function (e: number) {
          return dayjs(e, 'x').format(timeFormat);
        },
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: true,
      },
      data: normalizedData?.map(i => Number(i.ts)),
    },
    yAxis: {
      type: 'value',
      name: unit,
    },
    legend: {
      show: true,
      x: 'right',
      type: 'scroll',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 16,
      padding: [0, 8, 0, 0],
      data: ((normalizedData ?? [])?.[0]?.detailList ?? []).map((i: any, index: number) => {
        const color = getEnergyMediumColor({
          energyMediumId: i.energyMediumId,
          energyMediumName: i.energyMediumName,
          colorMap: energyMediumColorMap,
          fallbackIndex: index,
        });
        return {
          name: formatName(i.energyMediumName),
          icon: 'rect',
          itemStyle: {
            color,
          },
        };
      }),
    },
    series: ((normalizedData ?? [])?.[0]?.detailList ?? []).map((v: any, index: number) => {
      const color = getEnergyMediumColor({
        energyMediumId: v.energyMediumId,
        energyMediumName: v.energyMediumName,
        colorMap: energyMediumColorMap,
        fallbackIndex: index,
      });
      return {
        type: 'line',
        stack: 'Total',
        color,
        emphasis: {
          focus: 'series',
        },
        areaStyle: {
          opacity: 0.4,
        },
        name: formatName(v?.energyMediumName),
        data: (normalizedData ?? []).map(i => keepWithNumberOrNull(i.detailList?.[index]?.charges)),
      };
    }),
  };
};

const getTooltipByAll = (params: any, timeFormat?: string, unit?: string) => {
  const time = dayjs(Number(params[0].axisValue)).format(timeFormat);
  const allCostData = params?.map((i: any) => Number(i.data ?? 0));
  let allCost = allCostData.reduce((a: number, b: number) => Number(a) + Number(b), 0).toFixed(1);

  const styleStr = `display:flex;justify-content:space-between;align-items:center;font-size: 12px;font-weight: 500;color: rgba(255,255,255,0.65);line-height:17px;margin-top:8px;`;

  const items = params?.map((i: any) => {
    return `<div style='${styleStr}'>
        <div style='margin-right:30px' >
          <span style='width:8px;height:8px;background:${i.color}; border-radius:50%;display:inline-block' ></span>
          <span>${i.seriesName ?? '--'}</span>
        </div>
        <div  style='width:130px; display:flex; justify-content:space-between;align-items:center;'>
          <div style ='color: rgba(255,255,255,0.85); margin-right: 10px' >${formatOneDecimalOrDash(
            i.data
          )}${unit}</div>
          <div style ='color: rgba(255,255,255,0.85);'>${
            i.data ? ((i.data / Number(allCost)) * 100).toFixed(1) : '--'
          }%</div>
        </div>
      </div>`;
  });
  return (
    `<span style='font-size: 12px;color: rgba(255,255,255,0.65);line-height: 17px;'>${time}</span>` +
    ` <div style='display:flex; justify-content:space-between;align-items:center;font-size: 12px;font-weight: 500;color: rgba(255,255,255,0.85);' ><span>综合用能成本</span><span>${formatOneDecimalOrDash(
      allCost
    )}${unit}</span></div>` +
    `${items.join('')}`
  );
};

export const getChartOption = (itemDatas?: DataListItem['mediumCostList'], timeResolution?: IndexTimeActive) => {
  const { data: normalizedDatas, unit } = normalizeChartUnit(itemDatas, {
    valueKey: 'charges',
    baseUnit: '元',
  });

  const timeFormat = timeResolution === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'M月';
  return {
    backgroundColor: 'transparent',
    grid: {
      top: 30,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        const { data } = params?.[0];
        console.log('data', data, params);
        return `<p>${dayjs(data?.[0]).format(timeFormat)}</p><p>
          <span style='width:8px; height:8px; background:rgba(86,231,255,0.95);border-radius:50%;display:inline-block'></span> ${keepOneOrLine(
            data?.[1]
          )}${unit}</p>`;
      },
    },
    xAxis: {
      type: 'time',
      minInterval: 60 * 1000 * 60 * 24,
      axisLabel: {
        formatter: function (e: number) {
          return dayjs(e, 'x').format(timeFormat);
        },
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      name: unit,
      nameTextStyle: {
        align: 'left',
        padding: [0, 0, 0, -25],
      },
    },
    legend: {
      show: false,
    },
    series: {
      type: 'bar',
      emphasis: {
        focus: 'series',
      },
      data: normalizedDatas?.map(i => [i?.ts, keepWithNumberOrNull(i?.charges)]),
      itemStyle: {
        color: 'rgba(86,231,255,0.95)',
      },
    },
  };
};
